import { useCallback, useMemo, useState } from 'react';
import { startOfDay, addDays, format } from 'date-fns';
import { useQueries } from 'react-query';
import { SchedulerPage } from '@dispatch/web/features/scheduler-v2';
import type { SchedulerDateRange } from '@dispatch/web/features/scheduler-v2';
import {
  useNotification,
  useNotificationMutationFactory,
} from '@dispatch/web/ui';
import { useCurrentUser } from '@dispatch/web/auth';
import {
  useCompany,
  useGetCompanyAttributeOptions,
  useGetCompanyRoleOptions,
  useGetTaskFilters,
  useGetTaskFilterGroups,
  useUpsertTaskListFilter,
  useDeleteTaskFilter,
  useDeleteTaskFilterGroup,
  useUpsertTaskListFilterGroup,
  useFiltersOrderIndexBulkUpdate,
  useGroupsOrderIndexBulkUpdate,
  useGeneralDataContext,
  useFilter,
  useCurrentUserResource,
  useGetTimeZones,
  MapProvider,
  useGetGroupAttributes,
} from '@dispatch/shared/hooks';
import {
  MultiSelectOption,
  TaskFilterGroup,
  TasksListFilter,
  ResourceAttribute,
  Resource,
  Task,
} from '@dispatch/shared/types';
import { getResource } from '@dispatch/shared/services';
import {
  useSchedulerData,
  useEventHandlers,
  useSchedulerFilters,
} from '@dispatch/web/features/scheduler-v2';

// localStorage key for timezone persistence
const TIMEZONE_STORAGE_KEY = 'scheduler-timezone';

export function SchedulerViewPage() {
  // Compute fresh each render so date resets stay correct across midnight
  const today = startOfDay(new Date());
  const [dateRange, setDateRange] = useState<SchedulerDateRange>({
    startDate: today,
    endDate: addDays(today, 7),
  });

  const [timeZone, setTimeZone] = useState(() => {
    const stored = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (stored) {
      try {
        // Validate by attempting to construct a DateTimeFormat — throws on invalid timezone
        Intl.DateTimeFormat(undefined, { timeZone: stored });
        return stored;
      } catch {
        // Invalid timezone in localStorage — fall through to default
      }
    }
    return 'America/New_York';
  });
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const { userId, tenantId } = useCurrentUser();
  const { notifyError, notifySuccess } = useNotification();
  const {
    company,
    teams,
    offices,
    groups,
    perilOptions,
    regionOptions,
    uiValidationRules,
    featureFlags,
  } = useCompany();
  const { companyRoles } = useGetCompanyRoleOptions(company?.role);
  const { statesOptions, methodOfInspectionOptions } = useGeneralDataContext();
  const companyAttributes = useGetCompanyAttributeOptions(
    company?.attributeDefinition || []
  );
  const taskAttributeOptions = useMemo(() => {
    return company?.attributeDefinition?.flatMap((attribute) => {
      if (attribute.displayName.toLowerCase().includes('peril')) return [];
      if (attribute.displayName.toLowerCase().includes('assignment type'))
        return [];

      return (
        attribute?.attributeDefinitionValues?.map((value) => {
          const combinedTitle = `${attribute.displayName} = ${value.displayName}`;
          return {
            title: value.displayName.toLowerCase().includes('active')
              ? combinedTitle
              : value.displayName,
            value: value.integrationGUID,
          };
        }) || []
      );
    });
  }, [company?.attributeDefinition]);

  const { isAdjuster } = useCurrentUserResource();
  const { timeZones } = useGetTimeZones(tenantId || '');
  const { filters, quickFilters, setFilters } = useFilter();

  const { storedFilters, fetchStoredFilters } = useGetTaskFilters(userId || '');
  const { storedFilterGroups, fetchStoredFilterGroups } =
    useGetTaskFilterGroups(userId || '');

  const storedFiltersWithGroups = useMemo(() => {
    if (storedFilters && storedFilterGroups) {
      return [...storedFilterGroups, ...storedFilters];
    }
    return [];
  }, [storedFilters, storedFilterGroups]);
  const teamOptions = useMemo(() => {
    return (
      (teams?.map((team) => ({
        title: team.name,
        value: team.integrationGUID,
      })) as MultiSelectOption[]) || []
    );
  }, [teams]);

  const officeOptions = useMemo(() => {
    return (
      (offices?.map((office) => ({
        title: office.name,
        value: office.integrationGUID,
      })) as MultiSelectOption[]) || []
    );
  }, [offices]);

  const groupOptions = useMemo(() => {
    return (
      (groups?.map((group) => ({
        title: group.name,
        value: group.integrationGUID,
      })) as MultiSelectOption[]) || []
    );
  }, [groups]);

  const { groupAttributesData } = useGetGroupAttributes(tenantId);
  const attributeGroupOptions = useMemo(
    () =>
      (groupAttributesData ?? []).map((g) => ({
        title: g.attributeGroupName,
        value: g.id,
      })) as MultiSelectOption[],
    [groupAttributesData]
  );

  const filterOptionsLookup = useMemo(
    () => ({
      teamOptions: teamOptions || [],
      officeOptions: officeOptions || [],
      groupOptions: groupOptions || [],
      regionOptions: regionOptions || [],
      perilOptions: perilOptions || [],
      methodOfInspectionOptions: methodOfInspectionOptions || [],
      statesOptions: statesOptions || [],
      companyRoles: companyRoles || [],
      attributeGroupOptions: attributeGroupOptions || [],
    }),
    [
      teamOptions,
      officeOptions,
      groupOptions,
      regionOptions,
      perilOptions,
      methodOfInspectionOptions,
      statesOptions,
      companyRoles,
      attributeGroupOptions,
    ]
  );

  // ====================
  // Scheduler data hook
  // ====================
  const {
    data: { resources, events, calendars, unassignedTasks, unassignedCount },
    company: { rescheduleReasons, eventCategories },
    permissions: { canEditEvents, canCreateEvents },
    loading: {
      isLoadingSchedule,
      isLoadingUnassignedTasks,
      isFetchingNextPage,
    },
    pagination: { hasNextPage, loadMoreResources },
    refetch: { all: refetchAll },
    // Claim Owner capacity band (ADO #104914). The toggle state lives in
    // useSchedulerData because that hook owns the request it shapes; this page
    // only relays it to the presentational SchedulerPage.
    claimOwnerCapacity,
  } = useSchedulerData({
    dateRange,
    timeZone,
    enabled: true,
  });

  const resourceQueries = useQueries(
    selectedResourceIds.map((id) => ({
      queryKey: ['resource', id],
      queryFn: () => getResource(id),
      enabled: !!id,
      staleTime: 0,
      cacheTime: 0,
    }))
  );

  const isLoadingResources = resourceQueries.some((q) => q.isLoading);

  const multiSelected = useMemo(() => {
    if (isLoadingResources || selectedResourceIds.length === 0) {
      return [];
    }

    const loadedResources = resourceQueries
      .filter((q, index) => {
        if (!q.isSuccess || !q.data) return false;
        const expectedId = selectedResourceIds[index];
        const resource = q.data as Resource;
        return resource.integrationGUID === expectedId;
      })
      .map((q) => q.data as Resource);

    if (!loadedResources.length) return [];

    return loadedResources.map((resource) => ({
      ...resource,
      // Double cast: events are TaskWithLocation (scheduler-v2 internal type) filtered to tasks.
      // TaskWithLocation extends Task, so the cast is structurally safe.
      events: events.filter(
        (e) => e.resourceId === resource.integrationGUID && e.type === 'task'
      ) as unknown as Task[],
    }));
  }, [resourceQueries, selectedResourceIds, isLoadingResources, events]);

  const handleResourceSelectionChange = useCallback((resourceIds: string[]) => {
    setSelectedResourceIds(resourceIds);
  }, []);

  // ====================
  // Filter definitions hook
  // ====================
  const {
    dynamicFilters,
    quickFilterOptions,
    setLossStateFilterInput,
    setRsrcStateFilterInput,
  } = useSchedulerFilters({
    filterOptionsLookup,
    companyAttributes,
    taskAttributeOptions,
    isAdjuster,
    unassignedTasksCount: unassignedCount,
    qualifiedResourcesFnol: featureFlags?.qualifiedResourcesFnol,
  });

  // ====================
  // Filter mutation callbacks
  // ====================
  const onFilterComplete = useCallback(
    (response: { success: boolean }) => {
      if (response?.success) {
        notifySuccess('Filter Saved', 'Filter has been successfully saved');
        fetchStoredFilters?.();
        fetchStoredFilterGroups?.();
      } else {
        notifyError('Error', 'Filter was not saved');
      }
    },
    [notifySuccess, notifyError, fetchStoredFilters, fetchStoredFilterGroups]
  );

  const { upsertTaskFilters } = useUpsertTaskListFilter(
    onFilterComplete,
    userId || ''
  );
  const { orderIndexBulkUpdate } = useFiltersOrderIndexBulkUpdate(
    onFilterComplete,
    userId || ''
  );

  const onTaskFilterGroupReorder = useCallback(() => {
    fetchStoredFilterGroups?.();
  }, [fetchStoredFilterGroups]);

  const { groupsOrderIndexBulkUpdate } = useGroupsOrderIndexBulkUpdate(
    onTaskFilterGroupReorder,
    userId || ''
  );
  const { upsertTaskFilterGroup } = useUpsertTaskListFilterGroup(
    onFilterComplete,
    userId || ''
  );

  const { notifySuccess: successTaskDelete } = useNotificationMutationFactory(
    'Task Filter',
    'delete'
  );
  const onTaskFilterDelete = useCallback(() => {
    successTaskDelete();
    fetchStoredFilters?.();
  }, [successTaskDelete, fetchStoredFilters]);

  const { deleteTaskFilter } = useDeleteTaskFilter(
    userId || '',
    onTaskFilterDelete
  );

  const { notifySuccess: successTaskFilterGroupDelete } =
    useNotificationMutationFactory('Task Filter Group', 'delete');
  const onTaskFilterGroupDelete = useCallback(() => {
    successTaskFilterGroupDelete();
    fetchStoredFilterGroups?.();
  }, [successTaskFilterGroupDelete, fetchStoredFilterGroups]);

  const { deleteTaskFilterGroup } = useDeleteTaskFilterGroup(
    userId || '',
    onTaskFilterGroupDelete
  );

  // ====================
  // Filter handlers (mirror v1 TasksListPage verbatim — see PR 35109)
  // ====================
  const handleLossDateFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'> & { lossDate?: string[] }
  ) => {
    let lossDate = currentFilters?.lossDate;
    if (typeof lossDate === 'string') {
      lossDate = JSON.parse(lossDate);
    }
    if (lossDate && lossDate.length > 0) {
      if (lossDate[0]) {
        currentFilters['lossDateFrom'] =
          lossDate[0] && format(new Date(lossDate[0]), 'yyyy-MM-dd');
      }
      if (lossDate[1]) {
        currentFilters['lossDateTo'] =
          lossDate[1] && format(new Date(lossDate[1]), 'yyyy-MM-dd');
      }
    }
  };

  const handleNoticeDateFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'> & {
      noticeDate?: string[];
    }
  ) => {
    let noticeDate = currentFilters?.noticeDate;
    if (typeof noticeDate === 'string') {
      noticeDate = JSON.parse(noticeDate);
    }
    if (noticeDate && noticeDate.length > 0) {
      if (noticeDate[0]) {
        currentFilters['noticeDateFrom'] =
          noticeDate[0] && format(new Date(noticeDate[0]), 'yyyy-MM-dd');
      }
      if (noticeDate[1]) {
        currentFilters['noticeDateTo'] =
          noticeDate[1] && format(new Date(noticeDate[1]), 'yyyy-MM-dd');
      }
    }
  };

  const handleAppointmentDateFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'> & {
      appointmentDate?: string[];
    }
  ) => {
    let appointmentDate = currentFilters?.appointmentDate;
    if (typeof appointmentDate === 'string') {
      appointmentDate = JSON.parse(appointmentDate);
    }
    if (appointmentDate && appointmentDate.length > 0) {
      if (appointmentDate[0]) {
        currentFilters['appointmentDateFrom'] =
          appointmentDate[0] &&
          format(new Date(appointmentDate[0]), 'yyyy-MM-dd');
      }
      if (appointmentDate[1]) {
        currentFilters['appointmentDateTo'] =
          appointmentDate[1] &&
          format(new Date(appointmentDate[1]), 'yyyy-MM-dd');
      }
    }
  };

  const handleReceivedDateFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'> & {
      receivedDate?: string[];
    }
  ) => {
    let receivedDate = currentFilters?.receivedDate;
    if (typeof receivedDate === 'string') {
      receivedDate = JSON.parse(receivedDate);
    }
    if (receivedDate && receivedDate.length > 0) {
      if (receivedDate[0]) {
        currentFilters['receivedDateFrom'] =
          receivedDate[0] && format(new Date(receivedDate[0]), 'yyyy-MM-dd');
      }
      if (receivedDate[1]) {
        currentFilters['receivedDateTo'] =
          receivedDate[1] && format(new Date(receivedDate[1]), 'yyyy-MM-dd');
      }
    }
  };

  const handleResourceAttributeFilters = (
    currentFilters: Omit<TasksListFilter, 'pageSize'>
  ) => {
    let resourceAttributes = currentFilters?.resourceAttributes;
    if (typeof resourceAttributes === 'string') {
      resourceAttributes = JSON.parse(
        resourceAttributes
      ) as ResourceAttribute[];
    }

    const attributeFiltersWithValue =
      companyAttributes?.companyAttributes?.filter(
        (attribute) => currentFilters?.[attribute.value]
      );

    const attributes = resourceAttributes as ResourceAttribute[];

    attributeFiltersWithValue?.forEach((attribute) => {
      attributes?.push({
        attributeDefinition: attribute.title,
        values: JSON.parse(currentFilters?.[attribute.title] as string),
      });
    });

    if (attributes && attributes.length > 0) {
      currentFilters.resourceAttributes = JSON.stringify(attributes);
    }
  };

  const handleSaveFilters = useCallback(
    (values: unknown) => {
      const filtersToSave = values as TasksListFilter & {
        lossDate: string[];
        noticeDate: string[];
        appointmentDate: string[];
        receivedDate: string[];
      };
      handleLossDateFilters(filtersToSave);
      handleNoticeDateFilters(filtersToSave);
      handleAppointmentDateFilters(filtersToSave);
      handleReceivedDateFilters(filtersToSave);
      handleResourceAttributeFilters(filtersToSave);

      upsertTaskFilters(filtersToSave);
    },
    // handleResourceAttributeFilters closes over companyAttributes — must
    // refresh the memoized callback when those options change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyAttributes, upsertTaskFilters]
  );

  const filterOrderIndexBulkUpdate = useCallback(
    (values: (Omit<TasksListFilter, 'pageSize'> & { isNew?: boolean })[]) => {
      // v1 parity: in-place mutation of each entry, then pass the same array
      // reference to the bulk-update hook. forEach states the side-effect
      // intent without the .map misdirection.
      values.forEach((value) => {
        if (value.isNew) {
          handleLossDateFilters(value);
          handleNoticeDateFilters(value);
          handleAppointmentDateFilters(value);
          handleReceivedDateFilters(value);
          handleResourceAttributeFilters(value);
          delete value.isNew;
        }
      });
      orderIndexBulkUpdate(values);
    },
    // handleResourceAttributeFilters closes over companyAttributes — must
    // refresh the memoized callback when those options change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyAttributes, orderIndexBulkUpdate]
  );

  const handleSaveFilterGroup = useCallback(
    (values: TaskFilterGroup) => {
      upsertTaskFilterGroup(values);
    },
    [upsertTaskFilterGroup]
  );

  const groupsOrderIndexBulkUpdateHandler = useCallback(
    (values: TaskFilterGroup[]) => {
      groupsOrderIndexBulkUpdate(values);
    },
    [groupsOrderIndexBulkUpdate]
  );

  const handleDeleteFilter = useCallback(
    (filterId: string) => {
      deleteTaskFilter(filterId);
    },
    [deleteTaskFilter]
  );

  const handleDeleteFilterGroup = useCallback(
    (groupId: string) => {
      deleteTaskFilterGroup(groupId);
    },
    [deleteTaskFilterGroup]
  );

  // ====================
  // Event handlers hook
  // ====================
  const {
    handleEventUpdate,
    handleEventUpdateWithReason,
    handleEventCreate,
    handleEventDelete,
    handleTaskSchedule,
    rescheduleModal,
    closeRescheduleModal,
    isProcessing,
  } = useEventHandlers({
    timeZone: timeZone || '',
    tenantId: tenantId || '',
    onSuccess: refetchAll,
  });

  return (
    <MapProvider>
      <SchedulerPage
        // Data props
        resources={resources}
        events={events}
        calendars={calendars}
        unassignedTasks={unassignedTasks}
        unassignedCount={unassignedCount}
        rescheduleReasons={rescheduleReasons}
        eventCategories={eventCategories}
        canEditEvents={canEditEvents}
        canCreateEvents={canCreateEvents}
        isLoadingSchedule={isLoadingSchedule}
        isLoadingUnassignedTasks={isLoadingUnassignedTasks}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        loadMoreResources={loadMoreResources}
        refetchAll={refetchAll}
        // Event handler props
        handleEventUpdate={handleEventUpdate}
        handleEventUpdateWithReason={handleEventUpdateWithReason}
        handleEventCreate={handleEventCreate}
        handleEventDelete={handleEventDelete}
        handleTaskSchedule={handleTaskSchedule}
        rescheduleModal={rescheduleModal}
        closeRescheduleModal={closeRescheduleModal}
        isProcessing={isProcessing}
        // Filter props
        filters={filters}
        quickFilters={quickFilters}
        setFilters={setFilters}
        dynamicFilters={dynamicFilters}
        quickFilterOptions={quickFilterOptions}
        storedFiltersWithGroups={storedFiltersWithGroups}
        handleSaveFilters={handleSaveFilters}
        filterOrderIndexBulkUpdate={filterOrderIndexBulkUpdate}
        groupsOrderIndexBulkUpdateHandler={groupsOrderIndexBulkUpdateHandler}
        handleSaveFilterGroup={handleSaveFilterGroup}
        handleDeleteFilter={handleDeleteFilter}
        handleDeleteFilterGroup={handleDeleteFilterGroup}
        setLossStateFilterInput={setLossStateFilterInput}
        setRsrcStateFilterInput={setRsrcStateFilterInput}
        // Company props
        uiValidationRules={uiValidationRules}
        featureFlags={featureFlags}
        isAdjuster={isAdjuster}
        // Claim Owner capacity band props
        showClaimOwnerCapacity={claimOwnerCapacity.isRequested}
        onShowClaimOwnerCapacityChange={claimOwnerCapacity.setRequested}
        // Date/time props
        dateRange={dateRange}
        setDateRange={setDateRange}
        timeZone={timeZone}
        setTimeZone={setTimeZone}
        timeZoneOptions={timeZones}
        // Map props
        multiSelected={multiSelected}
        isLoadingMapResources={isLoadingResources}
        onResourceSelectionChange={handleResourceSelectionChange}
      />
    </MapProvider>
  );
}

export default SchedulerViewPage;
